import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ASSISTANT_ATTACHMENT_MAX_BYTES, ASSISTANT_ATTACHMENT_MIME_TYPES, ASSISTANT_ATTACHMENTS_PER_MESSAGE,
  assistantAttachmentMime, type AssistantAttachmentView,
} from '@repo/types'
import { api } from '@/lib/api'

/** Príloha rozpísanej správy — `view` chýba, kým sa súbor nahráva. */
export interface PendingAttachment {
  localId: string
  filename: string
  sizeBytes: number
  view?: AssistantAttachmentView
}

let seq = 0

async function uploadAttachment(file: File): Promise<AssistantAttachmentView> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/assistant/attachments', form, { headers: { 'Content-Type': 'multipart/form-data' } })
  return data
}

/** Kontrola pred uploadom — server overí to isté, toto len ušetrí zbytočné nahrávanie. */
function rejectReason(file: File): string | null {
  if (!ASSISTANT_ATTACHMENT_MIME_TYPES[assistantAttachmentMime(file.name, file.type)]) {
    return `„${file.name}“ sa nedá priložiť. Podporované sú obrázky (JPG, PNG, WebP, GIF), PDF, Word, Excel a PowerPoint.`
  }
  if (file.size > ASSISTANT_ATTACHMENT_MAX_BYTES) return `„${file.name}“ je väčší ako 20 MB.`
  return null
}

/** Prílohy rozpísanej správy asistenta: nahrávajú sa hneď po výbere, odošlú sa ako `attachmentIds`. */
export function useAssistantAttachments() {
  const [items, setItemsState] = useState<PendingAttachment[]>([])
  // Aktuálny zoznam aj mimo renderu — limit počtu sa počíta pred štartom uploadu.
  const itemsRef = useRef(items)
  const setItems = useCallback((update: (cur: PendingAttachment[]) => PendingAttachment[]) => {
    itemsRef.current = update(itemsRef.current)
    setItemsState(itemsRef.current)
  }, [])

  const add = useCallback((files: FileList | File[]) => {
    const accepted: File[] = []
    for (const file of Array.from(files)) {
      const reason = rejectReason(file)
      if (reason) toast.error(reason)
      else accepted.push(file)
    }
    const free = ASSISTANT_ATTACHMENTS_PER_MESSAGE - itemsRef.current.length
    if (accepted.length > free) toast.error(`K jednej správe môžete priložiť najviac ${ASSISTANT_ATTACHMENTS_PER_MESSAGE} súborov.`)
    for (const file of accepted.slice(0, Math.max(0, free))) {
      const localId = `att${++seq}`
      setItems((cur) => [...cur, { localId, filename: file.name, sizeBytes: file.size }])
      uploadAttachment(file).then(
        (view) => setItems((cur) => cur.map((i) => (i.localId === localId ? { ...i, view } : i))),
        // Chybu ukáže toast interceptora — neúspešný súbor z rozpísanej správy zmizne.
        () => setItems((cur) => cur.filter((i) => i.localId !== localId)),
      )
    }
  }, [setItems])

  const remove = useCallback((localId: string) => setItems((cur) => cur.filter((i) => i.localId !== localId)), [setItems])
  const clear = useCallback(() => setItems(() => []), [setItems])

  return {
    items,
    ready: items.flatMap((i) => i.view ?? []),
    uploading: items.some((i) => !i.view),
    add,
    remove,
    clear,
  }
}
